import React from 'react';
import { Box, Card, CardActionArea, CardContent, Typography } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';

export type CommunicationMethod = 'whatsapp' | 'email';

interface MethodChooserProps {
  onSelect: (method: CommunicationMethod) => void;
}

const METHODS: Array<{
  key: CommunicationMethod;
  label: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  disabled?: boolean;
}> = [
  {
    key: 'whatsapp',
    label: 'WhatsApp',
    description: 'Create a WhatsApp message template and send to a list of recipients.',
    icon: <WhatsAppIcon sx={{ fontSize: 48 }} />,
    color: '#25D366',
  },
  {
    key: 'email',
    label: 'Email',
    description: 'Create an HTML email template and send it via Elastic Email.',
    icon: <EmailIcon sx={{ fontSize: 48 }} />,
    color: '#1976D2',
  },
];

export default function MethodChooser({ onSelect }: MethodChooserProps) {
  return (
    <Box sx={{ maxWidth: 960, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 3, sm: 5 } }}>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
        Communication
      </Typography>
      <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4 }}>
        Choose a channel to broadcast a message from.
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          gap: 2.5,
        }}
      >
        {METHODS.map((method) => (
          <Card
            key={method.key}
            sx={{
              opacity: method.disabled ? 0.55 : 1,
              border: '1px solid',
              borderColor: 'divider',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
              '&:hover': method.disabled
                ? undefined
                : {
                    transform: 'translateY(-2px)',
                    borderColor: method.color,
                    boxShadow: `0 10px 28px ${method.color}33`,
                  },
            }}
          >
            <CardActionArea
              disabled={method.disabled}
              onClick={() => onSelect(method.key)}
              sx={{ p: 1, height: '100%' }}
            >
              <CardContent
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 1.25,
                  minHeight: 180,
                }}
              >
                <Box sx={{ color: method.color }}>{method.icon}</Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {method.label}
                  {method.disabled && (
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{
                        ml: 1,
                        px: 1,
                        py: 0.25,
                        bgcolor: 'action.selected',
                        borderRadius: 1,
                        fontWeight: 600,
                      }}
                    >
                      Coming soon
                    </Typography>
                  )}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {method.description}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>
    </Box>
  );
}
